import React from "react";
import { Phone, Mail, Clock, Facebook, Instagram, Twitter, Menu, X, ShoppingCart, Search, User, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useCustomer } from "../../context/CustomerContext";
import { getImageUrl } from "../../utils/imageUtils";
import { storePath } from "../../utils/storeUtils";

export const StoreHeader = ({
  store,
  theme,
  storeAppearance,
  subdomain,
  searchTerm,
  setSearchTerm,
  mobileMenuOpen,
  setMobileMenuOpen,
  collections = [],
}) => {
  const navigate = useNavigate();
  const { cartCount, setCartOpen } = useCart();
  const { customer } = useCustomer();

  return (
    <div className="sticky top-0 z-50 shadow-sm">
      {/* Announcement Bar */}
      {storeAppearance.announcement_enabled && storeAppearance.announcement_text && (
        <div
          className="py-2 px-4 text-center text-sm font-medium animate-in fade-in slide-in-from-top duration-500"
          style={{
            backgroundColor: theme.accent_color,
            color: 'white'
          }}
        >
          <span>{storeAppearance.announcement_text}</span>
        </div>
      )}

      <header className="bg-white border-b">
        {/* Row 1: Top Bar (Dark Red/Custom) */}
        <div className="hidden lg:block py-2 px-4 shadow-inner" style={{ backgroundColor: theme.primary_color, color: 'white' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-start text-[11px] font-bold tracking-[0.1em]">
            <div className="flex items-center gap-6">
              {store?.business_website && (
                <a
                  href={store.business_website.startsWith('http') ? store.business_website : `https://${store.business_website}`}
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="uppercase">Back to Funeral Home</span>
                </a>
              )}

              <div className="h-4 w-px bg-white/20"></div>

              {(storeAppearance.support_phone || storeAppearance.top_nav_phone) && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="uppercase">
                    Call Our Advisers {storeAppearance.support_phone || storeAppearance.top_nav_phone}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Main Header (White) */}
        <div className="py-4 px-4" style={{ backgroundColor: theme.header_bg_color }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            {/* Mobile Menu Button */}
            <button className="lg:hidden p-2" style={{ color: theme.text_color || theme.primary_color }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Logo and Nav */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                {store?.logo_url ? (
                  <img src={getImageUrl(store.logo_url)} alt={store?.business_name} className="h-10 w-auto object-contain" />
                ) : (
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: theme.primary_color }}>
                    <span className="text-white text-lg font-bold">{store?.business_name?.charAt(0)}</span>
                  </div>
                )}
                <div className="hidden sm:block">
                  {store?.show_business_name !== false && !store?.logo_url && (
                    <Link to={storePath(subdomain, "/")}>
                      <h1 className="text-xl font-bold" style={{ color: theme.text_color || theme.primary_color }}>
                        {store?.business_name}
                      </h1>
                    </Link>
                  )}
                </div>
              </div>

              {/* Navigation Section */}
              <nav className="hidden md:flex items-center gap-6">
                <Link
                  to={storePath(subdomain, "/")}
                  className="text-xs font-bold uppercase tracking-widest hover:opacity-70 transition-colors relative group"
                  style={{ color: theme.text_color }}
                >
                  Home
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full" style={{ backgroundColor: theme.accent_color }}></span>
                </Link>

                <Link
                  to={storePath(subdomain, "/shop")}
                  className="text-xs font-bold uppercase tracking-widest hover:opacity-70 transition-colors relative group border-l pl-6 border-slate-200"
                  style={{ color: theme.text_color }}
                >
                  Shop All
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-current transition-all group-hover:w-full" style={{ backgroundColor: theme.accent_color }}></span>
                </Link>


              </nav>
            </div>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-xs mx-4">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      navigate(searchTerm.trim() ? storePath(subdomain, `/shop?search=${encodeURIComponent(searchTerm.trim())}`) : storePath(subdomain, "/shop"));
                    }
                  }}
                  className="pl-10 w-full h-9 bg-slate-50 border-slate-200 focus-visible:ring-indigo-500 rounded-full text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Link
                to={storePath(subdomain, "/contact")}
                className="hidden md:flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity whitespace-nowrap px-4 border-r border-slate-200 mr-2"
                style={{ color: theme.text_color || theme.primary_color }}
              >
                Contact Us
              </Link>
              <Link to={customer ? storePath(subdomain, "/account") : storePath(subdomain, "/login")}>
                <Button variant="ghost" size="icon" className="hidden md:flex hover:bg-slate-50" title={customer ? "My Account" : "Sign In"}>
                  <User className="w-5 h-5" style={{ color: theme.text_color || theme.primary_color }} />
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="relative hover:bg-slate-50"
                onClick={() => setCartOpen(true)}
              >
                <ShoppingCart className="w-5 h-5" style={{ color: theme.text_color || theme.primary_color }} />
                {cartCount > 0 && (
                  <Badge
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-[10px] font-bold"
                    style={{ backgroundColor: theme.accent_color, color: 'white' }}
                  >
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
};

export default StoreHeader;
